import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import axios from 'axios';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export default function SectorFilterDialog({ onCategorySelect, resetSignal, placeholder  }) {
  const [open, setOpen] = useState(false);
  const [sectors, setSectors] = useState([]); // Stato per i dati dei settori dall'API

  const [selectedSectorCategories, setSelectedSectorCategories] = useState([]);
  const [selectedSectorSubCategories, setSelectedSectorSubCategories] = useState([]);

  const [selectedSector, setSelectedSector] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [sectorFilterLabel, setSectorFilterLabel] = useState("");

  // Effetto per resettare il filtro quando viene attivato il segnale
  useEffect(() => {
    if (resetSignal) {
      handleClose(); // Resetta i filtri
    }
  }, [resetSignal]);

  // Funzione per recuperare i dati dall'API
  const fetchSectors = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token non trovato. Effettua il login.');
      }
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/taxonomies?type=category`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSectors(response.data.data);
    } catch (error) {
      console.error('Errore nel recupero dei settori:', error);
    }
  };

  useEffect(() => {
    fetchSectors();
  }, []);

  // Preselezione basata sulla query string
  const handleCategoryPreselection = (categoryId) => {
    sectors.forEach((sector) => {
      sector.children?.forEach((category) => {
        if (category.id === categoryId) {
          handleSectorSelection(sector);
          handleCategorySelection(category);

          // Controlla se c'è un subCategory nella query string
          const subCategoryId = parseInt(new URLSearchParams(window.location.search).get("subCategory"));
          if (subCategoryId) {
            category.children?.forEach((subCategory) => {
              if (subCategory.id === subCategoryId) {
                handleSubCategorySelection(subCategory);
              }
            });
          }
        }
      });
    });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const categoryId = parseInt(urlParams.get("category"));
    if (categoryId) {
      handleCategoryPreselection(categoryId);
    }
  }, [sectors]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    // Resetta le selezioni quando si chiude il dialogo
    setSelectedSector(null);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedSectorCategories([]);
    setSelectedSectorSubCategories([]);
    setSectorFilterLabel("");
  };

  const handleSectorSelection = (sector) => {
    setSelectedSector(sector);
    setSectorFilterLabel(sector.name);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedSectorCategories(sector.children || []);
    setSelectedSectorSubCategories([]);
  };

  const handleCategorySelection = (category) => {
    setSelectedCategory(category);
    setSelectedSubCategory(null);
    setSectorFilterLabel(`${selectedSector.name} -> ${category.name}`);
    setSelectedSectorSubCategories(category.children || []);
  };

  const handleSubCategorySelection = (subCategory) => {
    setSelectedSubCategory(subCategory);
    setSectorFilterLabel(
      `${selectedSector.name} -> ${selectedCategory.name} -> ${subCategory.name}`
    );
  };

  const handleApply = () => {
    let selectedId = null;
    if (selectedSubCategory) {
      selectedId = selectedSubCategory.id;
    } else if (selectedCategory) {
      selectedId = selectedCategory.id;
    } else if (selectedSector) {
      selectedId = selectedSector.id;
    }
    if (onCategorySelect && selectedId) {
      onCategorySelect(selectedId);
    }
    setOpen(false);
  };

  const buttonStyles = {
    fontFamily: 'Urbanist, sans-serif',
    border: open ? '1px solid rgb(var(--global-color-primary))' : '1px solid rgb(255, 255, 255)',
    color: open ? 'rgb(var(--global-color-primary))' : 'rgb(255, 255, 255)',
    textTransform: 'none',
    fontSize: '1rem',
  };

  return (
    <React.Fragment>
      <Button
        variant="outlined"
        onClick={handleClickOpen}
        sx={{
          ...buttonStyles,
          position: 'relative',
          paddingRight: '30px' // spazio per la freccia
        }}
      >
        {sectorFilterLabel !== "" ? sectorFilterLabel : placeholder || "Sectors"}
      
        <ArrowDropDownIcon
          sx={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: open ? 'rgb(var(--global-color-primary))' : 'white',
          }}
        />
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth="lg"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px !important',
            overflow: 'hidden',
            backgroundColor: '#191919',
            border: "1.5px solid #C5FF55",
            padding: '15px',
          },
        }}
      >
        <DialogContent sx={{ fontFamily: 'Urbanist, sans-serif', backgroundColor: '#191919' }}>
          <div className="flex sm:flex-wrap gap-0">
            {/* Livello 1: Settori */}
            <div className="flex flex-col gap-2 justify-start items-start basis-1/3">
              <h1 className="text-xl">Sectors</h1>
              {sectors.map((sector) => (
                <Button
                  sx={{
                    color: selectedSector?.id === sector.id ? 'rgb(var(--global-color-primary))' : 'white',
                    padding: '0',
                    textTransform: 'none',
                    justifyContent: 'space-between',
                    fontFamily: 'Urbanist, sans-serif',
                    gap: '10px',
                  }}
                  key={sector.id}
                  onClick={() => {
                    handleSectorSelection(sector);
                  }}
                >
                  {sector.name}
                  {sector.children && sector.children.length > 0 && <span style={{ color: '#C5FF55' }}>&rsaquo;</span>}
                </Button>
              ))}
            </div>

            {/* Livello 2: Categorie */}
            {selectedSectorCategories.length > 0 && (
              <div className="flex flex-col gap-2 justify-start items-start basis-1/3">
                <h1 className="text-xl">Areas of Interest</h1>
                {selectedSectorCategories.map((category) => (
                  <Button
                    sx={{
                      color: selectedCategory?.id === category.id ? 'rgb(var(--global-color-primary))' : 'white',
                      padding: '0',
                      textTransform: 'none',
                      justifyContent: 'space-between',
                      fontFamily: 'Urbanist, sans-serif',
                      gap: '10px',
                    }}
                    key={category.id}
                    onClick={() => {
                      handleCategorySelection(category);
                    }}
                  >
                    {category.name}
                    {category.children && category.children.length > 0 && <span style={{ color: '#C5FF55' }}>&rsaquo;</span>}
                  </Button>
                ))}
              </div>
            )}

            {/* Livello 3: Sottocategorie */}
            {selectedSectorSubCategories.length > 0 && (
              <div className="flex flex-col gap-2 justify-start items-start basis-1/3">
                <h1 className="text-xl">Technologies</h1>
                {selectedSectorSubCategories.map((subCategory) => (
                  <Button
                    sx={{
                      color: selectedSubCategory?.id === subCategory.id ? 'rgb(var(--global-color-primary))' : 'white',
                      padding: '0',
                      textTransform: 'none',
                      justifyContent: 'space-between',
                      fontFamily: 'Urbanist, sans-serif',
                      gap: '10px',
                    }}
                    key={subCategory.id}
                    onClick={() => {
                      handleSubCategorySelection(subCategory);
                    }}
                  >
                    {subCategory.name}
                    {subCategory.children && subCategory.children.length > 0 && <span style={{ color: '#C5FF55' }}> &rsaquo;</span>}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions sx={{ fontFamily: 'Urbanist, sans-serif', backgroundColor: '#191919' }}>
          <Button variant="outlined" sx={{ borderColor: "rgb(var(--global-color-primary))", color: "#fff" }} onClick={handleClose}>
            Reset
          </Button>
          <Button variant="contained" sx={{ backgroundColor: "rgb(var(--global-color-primary))", color: "#000" }} onClick={handleApply}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
